import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Sparkles, LogOut, User, Menu, Search, X, Settings, FolderOpen } from 'lucide-react';
import axios from 'axios';
import NotificationBell from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth handled via Bearer token in axios interceptor (App.js)

function Navigation({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasNewStories, setHasNewStories] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ entries: [], stories: [], folders: [] });
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  
  const isActive = (path) => location.pathname === path;
  
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          const response = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
          setSearchResults(response.data);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults({ entries: [], stories: [], folders: [] });
      }
    }, 300);
    
    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);
  
  useEffect(() => {
    let interval = null;

    const checkNewStories = async () => {
      const token = localStorage.getItem('noyo_token');
      if (!token) return;

      try {
        const response = await axios.get(`${API}/experiences`);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const hasNew = response.data.some(exp =>
          new Date(exp.timestamp) > oneDayAgo && !exp.is_viewed
        );

        const lastVisit = localStorage.getItem('stories_last_visit');
        const hasVisitedSinceNew = lastVisit && hasNew && response.data.every(exp => {
          if (new Date(exp.timestamp) > oneDayAgo && !exp.is_viewed) {
            return new Date(exp.timestamp) < new Date(lastVisit);
          }
          return true;
        });

        setHasNewStories(hasNew && !hasVisitedSinceNew);
      } catch (error) {
        if (error.response && error.response.status === 401 && interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };

    if (user) {
      checkNewStories();
      interval = setInterval(checkNewStories, 60000);
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [user, location.pathname]);
  
  useEffect(() => {
    if (location.pathname === '/stories' || location.pathname.startsWith('/stories/')) {
      localStorage.setItem('stories_last_visit', new Date().toISOString());
      setHasNewStories(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSearchResultClick = async (type, item) => {
    setSearchOpen(false);
    setSearchQuery('');
    
    if (type === 'entry') {
      // Navigate to timeline with the correct year/month and highlight the entry
      const date = new Date(item.timestamp);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-indexed
      navigate(`/timeline?year=${year}&month=${month}&entry=${item.id}`);
    } else if (type === 'story') {
      // Get the folder ID for this story's topic_folder
      try {
        const foldersResponse = await axios.get(`${API}/folders`);
        const folder = foldersResponse.data.find(f => f.name === item.topic_folder);
        if (folder) {
          navigate(`/stories/${folder.id}?story=${item.id}`);
        } else {
          // Fallback to stories page
          navigate('/stories');
        }
      } catch (error) {
        navigate('/stories');
      }
    } else if (type === 'folder') {
      // Navigate directly to the folder
      navigate(`/stories/${item.id}`);
    }
  };

  const navLinks = [
    { path: '/', label: 'Journal', testId: 'nav-journal-link' },
    { path: '/stories', label: 'Stories', testId: 'nav-stories-link', hasNew: hasNewStories },
    { path: '/timeline', label: 'Timeline', testId: 'nav-timeline-link' },
  ];
  
  const totalResults = searchResults.entries.length + searchResults.stories.length + searchResults.folders.length;
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-border nav-header" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 lg:py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <BookOpen className="w-7 h-7 lg:w-10 lg:h-10 text-primary flex-shrink-0" />
            <div>
              <h1 className="text-xl lg:text-2xl font-serif font-semibold text-foreground">NOYO</h1>
              <p className="text-[10px] lg:text-xs text-muted-foreground tracking-wide hidden lg:block">Reflect Better. Converse Smarter.</p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 lg:px-6 py-2 text-sm font-medium tracking-wide rounded-full transition-colors flex items-center space-x-2 ${
                    isActive(link.path) || (link.path !== '/' && location.pathname.startsWith(link.path)) 
                      ? 'bg-primary text-white' 
                      : 'text-foreground hover:bg-secondary'
                  }`}
                  data-testid={link.testId}
                >
                  <span>{link.label}</span>
                  {link.hasNew && (
                    <span className="pulse-slow bg-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider" data-testid="nav-new-badge">
                      New
                    </span>
                  )}
                </Link>
              ))}
            </div>
            
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary rounded-full transition-colors"
              data-testid="search-button"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            
            {user && <NotificationBell />}
            
            {user && (
              <Link
                to="/settings"
                className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary rounded-full transition-colors"
                data-testid="settings-link"
              >
                <Settings className="w-5 h-5" />
              </Link>
            )}
            
            {user && (
              <div className="flex items-center space-x-3 border-l border-border pl-4">
                <div className="flex items-center space-x-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-8 h-8 text-muted-foreground" />
                  )}
                  <span className="text-sm text-foreground hidden lg:block">{user.name}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex lg:hidden items-center space-x-1">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary rounded-full transition-colors"
              data-testid="mobile-search-button"
              title="Search"
            >
              <Search className="w-5 h-5" />
            </button>
            <NotificationBell />
            {user && (
              <Link to="/settings" className="p-1" data-testid="mobile-profile-link">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-primary/30" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                )}
              </Link>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-foreground hover:bg-secondary rounded-lg transition-colors"
              data-testid="mobile-menu-button"
              title={mobileMenuOpen ? "Close menu" : "Menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="pt-4 pb-2 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                      isActive(link.path) || (link.path !== '/' && location.pathname.startsWith(link.path))
                        ? 'bg-primary text-white'
                        : 'text-foreground hover:bg-secondary'
                    }`}
                    data-testid={`mobile-${link.testId}`}
                  >
                    <span className="font-medium">{link.label}</span>
                    {link.hasNew && (
                      <span className="pulse-slow bg-accent text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                        New
                      </span>
                    )}
                  </Link>
                ))}
                {user && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border mt-2">
                    <div className="flex items-center space-x-2">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-8 h-8 text-muted-foreground" />
                      )}
                      <span className="text-sm text-foreground">{user.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`p-2 rounded-lg transition-colors ${
                          location.pathname === '/settings'
                            ? 'bg-primary text-white'
                            : 'text-muted-foreground hover:bg-secondary'
                        }`}
                        data-testid="mobile-settings-link"
                      >
                        <Settings className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={onLogout}
                        className="flex items-center space-x-2 text-red-500 hover:text-red-600 transition-colors"
                        data-testid="mobile-logout-button"
                      >
                        <LogOut className="w-5 h-5" />
                        <span className="text-sm">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 bg-white dark:bg-[#282828] border-b border-border shadow-lg"
              data-testid="search-modal"
            >
              <div className="max-w-2xl mx-auto p-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search folders, entries, and stories..."
                    className="w-full pl-12 pr-10 py-3 bg-white dark:bg-[#1A1A1A] border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 text-base text-gray-800 dark:text-[#E8E6E1] placeholder:text-gray-400 dark:placeholder:text-[#999]"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {searchQuery.length >= 2 && (
                  <div className="mt-4 max-h-[60vh] overflow-y-auto">
                    {isSearching ? (
                      <div className="text-center py-8">
                        <Sparkles className="w-6 h-6 text-primary animate-pulse mx-auto" />
                        <p className="text-sm text-muted-foreground mt-2">Searching...</p>
                      </div>
                    ) : totalResults === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">No results found for "{searchQuery}"</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {searchResults.folders.length > 0 && (
                          <div>
                            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                              Folders ({searchResults.folders.length})
                            </h3>
                            <div className="space-y-2">
                              {searchResults.folders.map((folder) => (
                                <button
                                  key={folder.id}
                                  onClick={() => handleSearchResultClick('folder', folder)}
                                  className="w-full text-left p-3 rounded-xl bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-colors"
                                  data-testid={`search-result-folder-${folder.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <FolderOpen className="w-5 h-5 text-primary" />
                                      <span className="text-sm font-medium text-foreground">{folder.name}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{folder.story_count} {folder.story_count === 1 ? 'story' : 'stories'}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {searchResults.entries.length > 0 && (
                          <div>
                            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                              Journal Entries ({searchResults.entries.length})
                            </h3>
                            <div className="space-y-2">
                              {searchResults.entries.map((entry) => (
                                <button
                                  key={entry.id}
                                  onClick={() => handleSearchResultClick('entry', entry)}
                                  className="w-full text-left p-3 rounded-xl bg-secondary/30 hover:bg-secondary transition-colors"
                                  data-testid={`search-result-entry-${entry.id}`}
                                >
                                  <p className="text-xs text-muted-foreground mb-1">
                                    {new Date(entry.timestamp).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                  <p className="text-sm text-foreground line-clamp-2">{entry.content}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {searchResults.stories.length > 0 && (
                          <div>
                            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                              Stories ({searchResults.stories.length})
                            </h3>
                            <div className="space-y-2">
                              {searchResults.stories.map((story) => (
                                <button
                                  key={story.id}
                                  onClick={() => handleSearchResultClick('story', story)}
                                  className="w-full text-left p-3 rounded-xl bg-secondary/30 hover:bg-secondary transition-colors"
                                  data-testid={`search-result-story-${story.id}`}
                                >
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                      {story.topic_folder}
                                    </span>
                                  </div>
                                  <p className="text-sm text-foreground line-clamp-2">{story.summary}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {searchOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setSearchOpen(false)}
          data-testid="search-backdrop"
        />
      )}
    </nav>
  );
}

export default Navigation;
